<template>
  <div>
    <div class="row">
      <div v-if="keinePassdatenBenoetigt" class="col-lg-8">
        <div class="teilnehmer-detail__column teilnehmer-detail__empty">
          Es werden keine Passdaten benötigt.
        </div>
      </div>

      <!-- PERSÖNLICHE DATEN -->
      <div v-if="!keinePassdatenBenoetigt" class="col-lg-4">
        <div class="teilnehmer-detail__column">
          <div class="teilnehmer-detail__header">
            <User :size="18" class="mr-2" style="color: #3699ff" />
            <h6 class="mb-0">Persönliche Daten</h6>
          </div>
          <div v-if="requiredPersoenlicheFelder.length === 0" class="text-muted">Nicht notwendig</div>
          <TeilnehmerFeld
            v-for="feld in requiredPersoenlicheFelder"
            :key="feld.key"
            :label="feld.label"
            :value="effektiverWert(feld.key)"
            :savedValue="teilnehmer[feld.key]"
            :changed="feld.key in bearbeiteteFelder"
            :type="feld.type"
            :options="feld.options"
            :nationalitaet="feld.key === 'nationalitaet' ? effektiverWert('nationalitaet') : null"
            :suffix="feld.key === 'geburtsdatum' && effektiverWert('geburtsdatum') ? `(${alter})` : ''"
            :searchable="feld.suchbar === true"
            :showStatus="feld.optional !== true"
            :readonly="!isEditAllowed || feld.readonly === true"
            :saveHandler="value => setBearbeiteteFelder(feld.key, value)"
          ></TeilnehmerFeld>
        </div>
      </div>

      <!-- PASSDATEN -->
      <div v-if="!keinePassdatenBenoetigt" class="col-lg-4">
        <div class="teilnehmer-detail__column">
          <div class="teilnehmer-detail__header">
            <Tickets :size="18" class="mr-2" style="color: #3699ff" />
            <h6 class="mb-0">Passdaten</h6>
          </div>
          <div v-if="sichtbarePassFelder.length === 0" class="text-muted">Nicht notwendig</div>
          <TeilnehmerFeld
            v-for="feld in sichtbarePassFelder"
            :key="feld.key"
            :label="feld.label"
            :value="effektiverWert(feld.key)"
            :savedValue="teilnehmer[feld.key]"
            :changed="feld.key in bearbeiteteFelder"
            :type="feld.type"
            :options="feld.options"
            :searchable="feld.suchbar === true"
            :showStatus="feld.optional !== true"
            :readonly="!isEditAllowed || feld.readonly === true"
            :placeholder="feld.placeholder || 'Bitte wählen'"
            :saveHandler="value => setBearbeiteteFelder(feld.key, value)"
          >
            <template v-if="feld.key === 'passGueltigkeitsdatum' && passWenigerAlsSiebenMonateGueltig" #hint>
              <span
                :title="$t('KUNDEN.PASS7MONTATEGIELTIG')"
                v-b-tooltip.hover
                class="bg-warning rounded p-1"
              >
                <i class="flaticon2-exclamation icon-1x text-white"></i>
              </span>
            </template>
          </TeilnehmerFeld>
        </div>
      </div>

      <!-- DOKUMENTE -->
      <div class="col-lg-4">
        <div class="teilnehmer-detail__column">
          <div class="teilnehmer-detail__header">
            <Paperclip :size="18" class="mr-2" style="color: #3699ff" />
            <h6 class="mb-0">Dokumente</h6>
          </div>
          <TeilnehmerDokumente
            :vorgangId="vorgangId"
            :teilnehmer="teilnehmer"
            :konfiguration="konfiguration"
            :dokumente="dokumente"
            :readonly="!isEditAllowed"
            :downloadAllowed="downloadAllowed"
            @uploaded="dokument => $emit('dokument-uploaded', dokument)"
            @deleted="dokument => $emit('dokument-deleted', dokument)"
          ></TeilnehmerDokumente>
        </div>
      </div>
    </div>

    <!-- Actionbar erscheint nur bei offenem Entwurf und speichert beide Spalten gemeinsam -->
    <div v-if="hasChanges && saveHandler" class="teilnehmer-detail__actionbar">
      <span class="teilnehmer-detail__actionbar-hinweis">
        {{ changedCount }} nicht gespeicherte {{ changedCount === 1 ? 'Änderung' : 'Änderungen' }}
      </span>
      <b-button class="dialog-btn-secondary" :disabled="isSaving" @click="verwerfen">Verwerfen</b-button>
      <b-button class="dialog-btn" variant="primary" :disabled="isSaving" @click="speichern">
        Speichern
      </b-button>
    </div>
  </div>
</template>

<script>
import TeilnehmerFeld from './teilnehmer-feld.vue';
import TeilnehmerDokumente from './teilnehmer-dokumente.vue';
import {
  getRequiredPersoenlicheFelder,
  getSichtbarePassFelder,
  berechneAlter,
  isPassLessThanSevenMonthsValid,
} from '@/core/vorgaenge/helpers/teilnehmer-passdaten.utils';
import { User, Tickets, Paperclip } from 'lucide-vue';

export default {
  name: 'TeilnehmerDetail',
  components: { TeilnehmerFeld, TeilnehmerDokumente, User, Tickets, Paperclip },
  props: {
    teilnehmer: { type: Object, required: true },
    vorgangId: { type: Number, required: true },
    konfiguration: { type: Object },
    reiseterminStartdatum: { type: [String, Object] },
    isEditAllowed: { type: Boolean, default: false },
    dokumente: { type: Array, default: () => [] },
    downloadAllowed: { type: Boolean, default: true },
    saveHandler: { type: Function, default: null },
  },
  data() {
    return {
      bearbeiteteFelder: {},
      isSaving: false,
    };
  },
  computed: {
    requiredPersoenlicheFelder() {
      return getRequiredPersoenlicheFelder(this.konfiguration);
    },
    sichtbarePassFelder() {
      return getSichtbarePassFelder(this.konfiguration);
    },
    // Trifft zu, wenn die Konfiguration weder persönliche Daten noch Passdaten verlangt
    keinePassdatenBenoetigt() {
      return this.requiredPersoenlicheFelder.length === 0 && this.sichtbarePassFelder.length === 0;
    },
    // Altersangabe und Pass-Warnung
    alter() {
      return berechneAlter(this.effektiverWert('geburtsdatum'));
    },
    passWenigerAlsSiebenMonateGueltig() {
      return isPassLessThanSevenMonthsValid(
        this.effektiverWert('passGueltigkeitsdatum'),
        this.reiseterminStartdatum
      );
    },
    hasChanges() {
      return Object.keys(this.bearbeiteteFelder).length > 0;
    },
    changedCount() {
      return Object.keys(this.bearbeiteteFelder).length;
    },
  },
  watch: {
    hasChanges(value) {
      this.$emit('dirty-changed', value);
    },
  },
  beforeDestroy() {
    this.$emit('dirty-changed', false);
  },
  methods: {
    // Liefert den anzuzeigenden Wert eines Feldes: den Entwurf, sonst den gespeicherten Stand
    effektiverWert(key) {
      return key in this.bearbeiteteFelder ? this.bearbeiteteFelder[key] : this.teilnehmer[key];
    },
    setBearbeiteteFelder(key, value) {
      if (value === this.teilnehmer[key]) {
        this.$delete(this.bearbeiteteFelder, key);
      } else {
        this.$set(this.bearbeiteteFelder, key, value);
      }
    },
    verwerfen() {
      this.bearbeiteteFelder = {};
    },
    async speichern() {
      this.isSaving = true;
      try {
        await this.saveHandler(this.teilnehmer, { ...this.bearbeiteteFelder });
        this.bearbeiteteFelder = {};
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.teilnehmer-detail__column {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 16px 24px;
  height: 100%;
  background: #fff;
  box-shadow: 0px 1px 3px 0px #0000001a;
}
.teilnehmer-detail__empty {
  color: var(--text-muted);
  font-size: 15px;
}
.teilnehmer-detail__header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}
.teilnehmer-detail__actionbar {
  // Sticky am unteren Rand
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 12px 16px;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--surface);
  box-shadow: var(--shadow-sm);
}
.teilnehmer-detail__actionbar-hinweis {
  margin-right: auto;
  color: var(--text-muted);
  font-family: Poppins, sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 15px;
}
</style>
